import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useDeletePickemMutation } from '@/api/pickems/deletePickem';
import { useGetLeaguesQuery } from '@/api/leagues/getLeagues';
import { SettingsPageTemplate } from '@/components/templates/SettingsPageTemplate';
import { useSettingsPage } from '@/hooks/useSettingsPage';
import { createTableActions } from '@/utils/actionFactory';
import { Plus, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useSearch } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateDisplay } from '@/components/datetime/DateDisplay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Route = createFileRoute('/dashboard/pickems/')({
  component: PickemsPage,
});

function PickemsPage() {
  const searchParams = useSearch({ from: '/dashboard/pickems/' }) as { league_id?: string };
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [leagueFilter, setLeagueFilter] = useState<string | undefined>(searchParams.league_id);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pickemToDelete, setPickemToDelete] = useState<any>(null);

  const { data: leagues = [] } = useGetLeaguesQuery();
  const {
    data: pickems = [],
    isLoading,
    refetch,
  } = useGetPickemsQuery({
    status: statusFilter || undefined,
  });
  const deletePickemMutation = useDeletePickemMutation();

  const formattedPickems = pickems
    .filter(pickem => !leagueFilter || (pickem as any).league_id === leagueFilter)
    .map((pickem, index) => ({
      ...pickem,
      id: pickem.pickem_id ? pickem.pickem_id.toString() : `temp-${index}`,
      label: pickem.title_en || pickem.title_fr || 'Untitled',
    }));

  const {
    searchQuery,
    setSearchQuery,
    filteredData: filteredPickems,
    handleEdit: handleEditPickem,
  } = useSettingsPage({
    data: formattedPickems,
    isLoading,
    refetch,
    settingsKey: 'pickems',
    editRoutePath: '/dashboard/pickems/$id',
    getSearchFields: (pickem: any) => [
      pickem.title_en || '',
      pickem.title_fr || '',
      pickem.description_en || '',
      pickem.description_fr || '',
      pickem.pickem_type || '',
    ],
  });

  const handleDeleteClick = (pickem: any) => {
    setPickemToDelete(pickem);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (pickemToDelete) {
      await deletePickemMutation.mutateAsync(pickemToDelete.id);
      setDeleteDialogOpen(false);
      setPickemToDelete(null);
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    const variants: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
      ACTIVE: 'default',
      COMPLETE: 'secondary',
      CANCELLED: 'destructive',
    };

    return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
  };

  const getLeagueName = (leagueId?: string) => {
    if (!leagueId) return '-';
    const league = leagues.find(l => l.league_id === leagueId);
    return league?.name_en || league?.name_fr || league?.title || '-';
  };

  const columns = [
    {
      id: 'league_id',
      name: 'League',
      render: (pickem: { league_id?: string }) => (
        <Link
          to="/dashboard/leagues/$id"
          params={{ id: pickem.league_id || '' }}
          className="text-blue-700 hover:text-blue-900 hover:underline font-medium"
        >
          {getLeagueName(pickem.league_id)}
        </Link>
      ),
    },
    {
      id: 'title_en',
      name: 'Title',
      className: 'font-medium',
      render: (pickem: { title_en?: string; title_fr?: string }) =>
        pickem.title_en || pickem.title_fr || 'Untitled',
    },

    {
      id: 'pickem_status',
      name: 'Status',
      render: (pickem: { pickem_status?: string; status?: string }) =>
        getStatusBadge(pickem.pickem_status || pickem.status),
    },
    {
      id: 'start',
      name: 'Start Date',
      render: (pickem: { start?: string; start_utc?: string }) => {
        const dateStr = pickem.start || pickem.start_utc;
        if (!dateStr) return '-';

        const formattedDate = new Date(dateStr).toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        return <DateDisplay utcDate={dateStr}>{formattedDate}</DateDisplay>;
      },
    },
    {
      id: 'entry_count',
      name: 'Entries',
      render: (pickem: { entry_count?: number }) => (
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {pickem.entry_count !== undefined ? pickem.entry_count.toLocaleString() : '-'}
        </span>
      ),
    },
    {
      id: 'question_count',
      name: 'Questions',
      render: (pickem: { question_count?: number }) => (
        <span className="text-gray-700 dark:text-gray-300 font-medium">
          {pickem.question_count !== undefined ? pickem.question_count.toLocaleString() : '-'}
        </span>
      ),
    },
  ];

  const actions = createTableActions({
    onEdit: handleEditPickem,
    onDelete: handleDeleteClick,
  });

  const headerActions = (
    <div className="flex gap-2 items-center">
      <Select
        value={leagueFilter || 'all'}
        onValueChange={value => setLeagueFilter(value === 'all' ? undefined : value)}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Filter by league" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Leagues</SelectItem>
          {leagues.map(league => (
            <SelectItem key={league.league_id} value={league.league_id}>
              {league.name_en || league.name_fr || league.title || 'Unnamed League'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Link to="/dashboard/pickems/$id" params={{ id: 'new' }} search={{ league_id: undefined }}>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Pickem
        </Button>
      </Link>
    </div>
  );

  const statusTabs = (
    <Tabs
      value={statusFilter || 'all'}
      onValueChange={value => setStatusFilter(value === 'all' ? '' : value)}
    >
      <TabsList>
        <TabsTrigger value="all">All</TabsTrigger>
        <TabsTrigger value="active">Active</TabsTrigger>
        <TabsTrigger value="complete">Completed</TabsTrigger>
        <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
      </TabsList>
    </Tabs>
  );

  return (
    <>
      <SettingsPageTemplate
        title="Pickems"
        subtitle="Manage pickem competitions and contests"
        data={filteredPickems}
        isLoading={isLoading}
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search pickems by title or type..."
        columns={columns}
        actions={actions}
        headerActions={headerActions}
        emptyMessage="No pickems found"
        emptyState={{
          icon: Target,
          title: 'No pickems yet',
          description: 'Create your first pickem competition to engage your users.',
          actionLabel: 'Add Pickem',
          onAction: () => handleEditPickem({ id: 'new' } as any),
        }}
        breadcrumbs={<Breadcrumbs />}
        topContent={statusTabs}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pickem</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting pickems with entries may upset your users! Are you sure you want to delete "
              {pickemToDelete?.title_en || pickemToDelete?.title_fr}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
